import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { parse } from 'date-fns';
import format from './../../utilities/format';
import PriceGraphItem from './PriceGraphItem';
import translate from './../../utilities/translator';
import { DATE_DDMMYYYY, IMAGE_CDN } from '../../constants/constants';
import Counter from '../../components/Counter';
import { selectDestinationGraph, setFirstVisibleDate, setPriceGraphNights } from './priceGraphSlice';
import useDebounce from './../../hooks/useDebounce';
import { parseBoldText } from '../../utilities/helpers';
import { pushEvent } from '../../utilities/analytics';
import { PRICE_GRAPH_CHANGE_NIGHTS, PRICE_GRAPH_CHANGE_MONTH } from '../../constants/analyticEvents';
import { selectDateFilter, selectMonthAhead } from '../mainFilter/selectors';
import { DESKTOP_GRAPH_ITEM_WIDTH } from './constants';
import FadeTransition from '../../components/animations/FadeTransition';
import { useScrollBooster } from './hooks/useScrollBooster';

const PriceGraphDesktop = ({
  dates,
  applyDates,
  nightsLimit,
  destination,
  filterDate,
}) => {
  const listRef = useRef(null);
  const viewport = useRef(null);
  const isRtl = document.dir === 'rtl';
  const dispatch = useDispatch();
  const { startDate } = useSelector(selectDateFilter);
  const { nights, pending: graphLoading } = useSelector(selectDestinationGraph);
  const monthAhead = useSelector(selectMonthAhead);
  const debounceNights = useDebounce(nights, 500);
  const [scrollLoading, setScrollLoading] = useState(true);
  const [shouldScrollToDate, setShouldScrollToDate] = useState(false);
  const [monthLabel, setMonthLabel] = useState('');
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 30 })
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const months = useMemo(() => [
    ...new Set(
      dates.map((date) =>
        format(parse(date.start, DATE_DDMMYYYY, new Date()), 'MM/yyyy')
      )
    ),
  ], [dates]);

  const calculateVisibleRange = useCallback((position) => {
    const node = document.querySelector('.price-graph__item');
    if (!node) return;

    const nodeWidth = node.clientWidth;
    const visibleElements = Math.round(viewport.current?.clientWidth / nodeWidth);
    const startElement = Math.floor(Math.abs(position.x) / nodeWidth);

    setVisibleRange({
      start: startElement,
      end: startElement + visibleElements
    })
  }, [])

  useEffect(() => {
    if (!scrollLoading) {
      dispatch(setFirstVisibleDate(dates[visibleRange.start]?.start));
    }
  }, [dispatch, visibleRange, scrollLoading, dates])

  const onScrollUpdate = useCallback(({ isMoving, position }) => {
    if (
      isMoving ||
      window.innerWidth < 1025 ||
      !listRef.current
    ) {
      return undefined;
    }

    calculateVisibleRange(position);

    const list = listRef.current?.childNodes;
    const months = [...list].map((month) => ({
      name: month.querySelector('.price-graph__month-label').textContent,
      width: month.clientWidth,
    }));
    const calcMonths = months.map((month, i) => {
      const start = months.slice(0, i).reduce((a, b) => a + b.width, 0);

      return {
        name: month.name,
        position: {
          start,
          end: months[i].width + start,
        },
      };
    });

    calcMonths.forEach((month) => {
      if (position.x === 0) {
        return setMonthLabel(months[0].name);
      }

      if (
        Math.round(Math.abs(position.x)) >= month.position.start &&
        Math.round(Math.abs(position.x)) < month.position.end
      ) {
        return setMonthLabel(month.name);
      }
    })
  }, [calculateVisibleRange]);

  const scrollbooster = useScrollBooster(viewport, {
    scrollMode: 'native',
    direction: 'horizontal',
    rtl: isRtl,
    friction: 0.2,
    shouldScroll: false,
  }, onScrollUpdate);

  // TODO: Move to index.js
  useEffect(() => {
    dispatch(setPriceGraphNights(debounceNights));
  }, [debounceNights, destination.id, dispatch]);

  useEffect(() => {
    const nonZeroPrices = dates.slice(visibleRange.start, visibleRange.end).filter(({ avgPrice }) => avgPrice !== 0);

    if (nonZeroPrices.length > 0) {
      const min = nonZeroPrices.reduce((min, currentValue) => Math.min(min, currentValue.avgPrice), nonZeroPrices[0].avgPrice);
      const max = nonZeroPrices.reduce((max, currentValue) => Math.max(max, currentValue.avgPrice), 0);

      if (priceRange.min !== min || priceRange.max !== max) {
        setPriceRange({ min, max });
      }
    }
  }, [dispatch, dates, priceRange, visibleRange]);

  useLayoutEffect(() => {
    const nodeWidth = DESKTOP_GRAPH_ITEM_WIDTH;
    const index = dates.indexOf(
      dates.filter((item) => {
        const startDateArray = item.start.split('/');
        return (
          +startDateArray[1] === +filterDate.month &&
          +startDateArray[0] === +filterDate.date
        );
      })[0]
    );

    if (!scrollbooster || index < 0) return;

    setScrollLoading(true);

    // Set active element to center of view.
    const xPosition = isRtl
      ? -(nodeWidth * Math.abs(index) - Math.floor(((scrollbooster.viewport.width / 2) / nodeWidth) - 1) * nodeWidth)
      : nodeWidth * Math.abs(index) - Math.round((scrollbooster.viewport.width / 2) / nodeWidth - 1) * nodeWidth;

    if (shouldScrollToDate) {
      const getXPosition = () => {
        if (isRtl) {
          return Math.abs(index) === 1 ? 0 : xPosition >= 0 ? 0 : xPosition;
        } else {
          return Math.abs(index) === 1 ? 0 : xPosition <= 0 ? 0 : xPosition;
        }
      }
      scrollbooster.scrollTo({
        x: getXPosition(),
        y: 0,
      });
      scrollbooster.updateMetrics();
      setShouldScrollToDate(false);
    }

    const scrollTimeout = setTimeout(() => setScrollLoading(false), 500)

    return () => {
      clearTimeout(scrollTimeout)
    }
  }, [filterDate, dates, shouldScrollToDate, scrollbooster, isRtl]);

  // activate scroll to date only if date filter was changed.
  useEffect(() => {
    setShouldScrollToDate(true);
  }, [filterDate])

  const changeNightsOnClick = (nights) => {
    dispatch(setPriceGraphNights(nights));
    pushEvent(PRICE_GRAPH_CHANGE_NIGHTS, nights);
  };

  const getProgress = useCallback((value) => {
    const range = priceRange.max - priceRange.min;

    if (value === 0) {
      return 0;
    }

    if (range === 0) {
      return 10;
    }

    return (60 / range) * (value - priceRange.min) + 10;
  }, [priceRange]);

  const slides = useMemo(() => {
    let slides = []
    months.forEach((month) => {
      slides.push(
        dates.filter(
          (date) =>
            format(parse(date.start, DATE_DDMMYYYY, new Date()), 'MM/yyyy') === month
        )
      );
    });

    return slides;
  }, [dates, months]);

  const next = () => {
    setScrollLoading(true);
    let xPos = scrollbooster.getState().position.x + DESKTOP_GRAPH_ITEM_WIDTH * 7;
    if (isRtl) {
      xPos = scrollbooster.getState().position.x - DESKTOP_GRAPH_ITEM_WIDTH * 7;
    }

    if (isRtl) {
      if (xPos < -(scrollbooster.content.width - scrollbooster.viewport.width))
        xPos = -scrollbooster.content.width + scrollbooster.viewport.width;
    } else {
      if (xPos > scrollbooster.content.width - scrollbooster.viewport.width)
        xPos = scrollbooster.content.width - scrollbooster.viewport.width;
    }

    scrollbooster.scrollTo({ x: xPos, y: 0 });
    scrollbooster.updateMetrics();
    pushEvent(PRICE_GRAPH_CHANGE_MONTH);
    setTimeout(() => setScrollLoading(false), 500)
  };

  const previous = () => {
    setScrollLoading(true);
    let xPos = scrollbooster.getState().position.x - DESKTOP_GRAPH_ITEM_WIDTH * 7;

    if (isRtl) {
      xPos = scrollbooster.getState().position.x + DESKTOP_GRAPH_ITEM_WIDTH * 7;
      if (xPos > 0) xPos = 0;
    } else {
      if (xPos < 0) xPos = 0;
    }

    scrollbooster.scrollTo({ x: xPos, y: 0 });
    scrollbooster.updateMetrics();
    pushEvent(PRICE_GRAPH_CHANGE_MONTH);
    setTimeout(() => setScrollLoading(false), 500)
  };

  const isDateSelected = (date) => startDate === date.start;

  const graphMonthLabel = startDate
    ? format(parse(startDate, DATE_DDMMYYYY, new Date()), 'MMMM')
    : format(parse(months[0], 'MM/yyyy', new Date()), 'MMMM')

  const priceGraphLabel = translate('holidayFinder.priceGraph.subtitle_desktop', {
    nights,
    destination: destination.name,
    month: graphMonthLabel,
  });

  return (
    <div className="col-lg-12">
      <div
        className={classNames({
          'price-graph': true,
          'price-graph--loading': graphLoading,
        })}
      >
        <div className="price-graph__header">
          <img src={`${IMAGE_CDN}/price-graph.png`} alt="" />
          <div>
            <div className="price-graph__title">
              {translate('holidayFinder.priceGraph.title_desktop')}
            </div>
            <div className="price-graph__subtitle">
              {parseBoldText(priceGraphLabel)}
            </div>
          </div>
        </div>
        <div className="carousel">
          {scrollbooster && (
            <div className="carousel-arrows__arrow" onClick={previous}>
              <i className="ri-arrow-left-s-line" />
            </div>
          )}
          <div className="price-graph__month-label observable">
            {monthLabel}
          </div>
          <div className="list-wrap" ref={viewport}>
            <div className="list" ref={listRef}>
              {slides.slice(0, monthAhead).map((slide, slideIndex) => (
                <div className="price-graph__slide" key={slideIndex}>
                  <div className="price-graph__month-label">
                    {format(
                      parse(slide[0].start, DATE_DDMMYYYY, new Date()),
                      'MMM yyyy'
                    )}
                  </div>
                  <div className="list">
                    {slide.map((date, itemIndex) => (
                      <PriceGraphItem
                        key={`${slideIndex}-${itemIndex}`}
                        date={date}
                        getProgress={getProgress}
                        scrollbooster={scrollbooster}
                        onApply={applyDates}
                        nights={nights}
                        selected={isDateSelected(date)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {scrollbooster && (
            <div className="carousel-arrows__arrow" onClick={next}>
              <i className="ri-arrow-right-s-line" />
            </div>
          )}
        </div>
        <div className="price-graph__bottom">
          <div className="non-direct-badge">
            <i className="non-direct-dot" />
            {translate('holidayFinder.priceGraph.non_direct_flight')}
          </div>
          <div className="price-graph__nights-counter">
            {nights} {translate('holidayFinder.priceGraph.item_nights_plural', { count: nights })}
            <Counter
              showValue={false}
              value={nights}
              min={nightsLimit.min}
              max={nightsLimit.max}
              onChange={changeNightsOnClick}
            />
          </div>
        </div>
        <FadeTransition in={graphLoading} unmountOnExit>
          <div className="price-graph__loader" />
        </FadeTransition>
      </div>
    </div>
  );
}

export default PriceGraphDesktop;
